// "5 échecs en 10 minutes" (SPEC-15), counted on the phone to show the
// message and hold the button. It protects nothing: anyone can clear a
// browser's storage. Firebase's own throttle, on the server, is the
// protection (the card's Dev notes, "Décidé sans question").
//
// Kept in localStorage so a reload does not hand back five fresh tries.
// Storage that throws (a private window, blocked site data) only costs the
// count: the screen still works.

export const SIGN_IN_MAX_FAILURES = 5;
export const SIGN_IN_FAILURE_WINDOW_MS = 10 * 60_000;
export const SIGN_IN_LOCKOUT_MS = 10 * 60_000;

const KEY = "pyf.signIn.attempts";

interface Attempts {
  /** Epoch ms of the recent failures, at most SIGN_IN_MAX_FAILURES. */
  failures: number[];
  /** Epoch ms when the button comes back, or null. */
  lockedUntil: number | null;
}

const EMPTY: Attempts = { failures: [], lockedUntil: null };

function read(): Attempts {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(KEY) ?? "null");
    if (typeof parsed !== "object" || parsed === null) return EMPTY;
    const { failures, lockedUntil } = parsed as Partial<Attempts>;
    return {
      failures: Array.isArray(failures) ? failures.filter((at) => typeof at === "number").slice(-SIGN_IN_MAX_FAILURES) : [],
      lockedUntil: typeof lockedUntil === "number" ? lockedUntil : null,
    };
  } catch {
    return EMPTY;
  }
}

function write(attempts: Attempts): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(attempts));
  } catch (error) {
    console.warn("[sign-in] the attempt count could not be stored", { kind: error instanceof Error ? error.name : typeof error });
  }
}

/** When the button comes back, or null when it is not held. */
export function lockedUntil(now: number): number | null {
  const { lockedUntil: until } = read();
  return until !== null && until > now ? until : null;
}

/** Counts a refused password; the fifth within the window holds the button. Returns lockedUntil(now). */
export function recordFailure(now: number): number | null {
  const failures = [...read().failures.filter((at) => now - at < SIGN_IN_FAILURE_WINDOW_MS), now];
  if (failures.length >= SIGN_IN_MAX_FAILURES) {
    write({ failures: [], lockedUntil: now + SIGN_IN_LOCKOUT_MS });
  } else {
    write({ failures, lockedUntil: null });
  }
  return lockedUntil(now);
}

/** Holds the button now: Firebase's throttle answered before the phone's count did. */
export function lockNow(now: number): number {
  const until = now + SIGN_IN_LOCKOUT_MS;
  write({ failures: [], lockedUntil: until });
  return until;
}

/** A sign-in that worked, or a new password set, starts the count over. */
export function clearAttempts(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing stored, nothing to clear.
  }
}
